import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Status values supported by the chip. Extensible — keep adding statuses here
 * as F12 (disputes) and other features need them; map them to a tone below.
 */
export type StatusChipStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'expiring'
  | 'submitted'
  | 'in_review';

export type StatusChipSize = 'sm' | 'md';

export interface StatusChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly status: StatusChipStatus;
  readonly size?: StatusChipSize;
  /** When true, the leading dot pulses gently to draw the eye. */
  readonly pulse?: boolean;
  /** Optional label override. Defaults to a humanised version of `status`. */
  readonly label?: string;
}

/** semantic tone mapping → resolves to design-token colours via Tailwind utilities */
type Tone = 'warning' | 'accent' | 'success' | 'danger';

const STATUS_TONE: Record<StatusChipStatus, Tone> = {
  pending: 'warning',
  in_review: 'accent',
  submitted: 'accent',
  approved: 'success',
  rejected: 'danger',
  expiring: 'warning',
};

const TONE_CLASSES: Record<Tone, { bg: string; text: string; dot: string }> = {
  warning: { bg: 'bg-warning/10', text: 'text-ink', dot: 'bg-warning' },
  accent: { bg: 'bg-accent/10', text: 'text-ink', dot: 'bg-accent' },
  success: { bg: 'bg-success/10', text: 'text-ink', dot: 'bg-success' },
  danger: { bg: 'bg-danger/10', text: 'text-ink', dot: 'bg-danger' },
};

const SIZE_CLASSES: Record<StatusChipSize, { box: string; dot: string; text: string }> = {
  sm: { box: 'h-5 px-2 gap-1.5', dot: 'size-1.5', text: 'text-[10.5px]' },
  md: { box: 'h-6 px-2.5 gap-2', dot: 'size-2', text: 'text-[11.5px]' },
};

function defaultLabel(status: StatusChipStatus): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Small pill that surfaces a verification, dispute or workflow status.
 * Used by F09 and reused by F12 disputes — keep the API extensible.
 */
export function StatusChip({
  status,
  size = 'md',
  pulse = false,
  label,
  className,
  ...rest
}: StatusChipProps) {
  const tone = TONE_CLASSES[STATUS_TONE[status]];
  const sz = SIZE_CLASSES[size];
  const text = label ?? defaultLabel(status);
  return (
    <span
      role="status"
      aria-label={text}
      className={cn(
        'inline-flex items-center rounded-full font-bold uppercase tracking-wider font-mono whitespace-nowrap align-middle',
        sz.box,
        sz.text,
        tone.bg,
        tone.text,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'rounded-full shrink-0',
          sz.dot,
          tone.dot,
          pulse ? 'motion-safe:animate-pulse' : undefined,
        )}
      />
      <span>{text}</span>
    </span>
  );
}

export default StatusChip;
